import { getTimezoneAbbreviationFromIANA } from '@/utils/datetimes/misc';
import { gql, skipToken, useMutation, useSuspenseQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { Moment } from 'moment-timezone';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import invariant from 'tiny-invariant';

import { AxleCallout } from '../../../components/displayFields/AxleCallout';
import { AxleLabel } from '../../../components/formFields/AxleLabel';
import { AxleCheckboxField } from '../../../components/formFields/checkboxes';
import { AxleMomentTimeField } from '../../../components/formFields/datetimeFields';
import {
    AxleDialog,
    AxleDialogActions,
    AxleDialogContent,
    AxleDialogPrimaryButton,
} from '../../../components/layout/AxleDialog';
import { HorizontalStackInnerGrow } from '../../../components/utils/linearLayouts';
import { SmartPotentialVisitAssigneesField } from '../../../components/visits/assignees/SmartVisitOrderAssigneesField';
import { usePotentialAssigneesStateHelper } from '../../../components/visits/assignees/helpers';
import { QhpSimple } from '../../../fetching/sharedSchemas/basicSchemas';
import { AxleSuspense, SECTION_SKELETON_HEIGHT } from '../../../fetching/suspense';
import { useTimeslotAlignmentMinutes } from '../../../hooks/datetimes/useTimeslotAlignmentMinutes';
import { successToast } from '../../../toast';
import { Eligibility } from '../../../types/eligibility';
import { AddressInput } from '../../../utils/addresses';
import { useInputState, useJointInputState } from '../../../utils/inputState';
import { ReturnComponent } from '../../../utils/reactTypes';
import { useFallbackToNewUuid } from '../../../utils/uuids';
import { FlexibleSchedulingDialogsCalendarWrapper } from '../../ScheduleVisitPages/FlexibleSchedulingDialogsCalendarWrapper';
import { useVisitBookingPageContext } from '../VisitBookingPageProvider';
import {
    CREATE_CONFIRMED_VISIT_FROM_VISIT_REQUEST_MUTATION,
    CREATE_CONFIRMED_VISIT_MUTATION,
    CreateConfirmedVisitFromVisitRequestMutationType,
    CreateConfirmedVisitMutationType,
} from './BookConfirmed';

const CLINICIAN_FRAGMENT = gql`
    fragment ClinicianFragment on Clinician {
        id
        firstName
        lastName
        logoUrl
        timezone
    }
`;

type ClinicianFragmentType = {
    id: string;
    firstName: string;
    lastName: string;
    timezone: string;
    logoUrl: string | null;
};

const clinicianFragmentToQhpSimple = (clinician: ClinicianFragmentType): QhpSimple => ({
    uuid: clinician.id,
    name: `${clinician.firstName} ${clinician.lastName}`,
    logo_url: clinician.logoUrl,
    timezone: clinician.timezone,
    first_name: clinician.firstName,
    last_name: clinician.lastName,
    // TODO: add teams once they are in the graph
    teams: [],
    // NOTE: Address is intentially left blank. It's not needed
    // but we need a `QhpSimple` shape which requires it.
    address: null,
});

const FLEXIBLE_VISIT_AVAILABILITY_FRAGMENT = gql`
    fragment FlexibleVisitAvailabilityFragment on Availability {
        potentialAppointmentAssignments {
            clinician {
                ...ClinicianFragment
            }
            clinicianAppointmentId
        }
        timeOptions {
            start
            end
        }
        timezone
    }
    ${CLINICIAN_FRAGMENT}
`;

type FlexibleAvailabilityType = {
    potentialAppointmentAssignments: {
        clinician: ClinicianFragmentType | null;
        clinicianAppointmentId: string;
    }[];
    timeOptions: {
        start: string;
        end: string;
    }[];
    timezone: string;
};

const GET_FLEXIBLE_AVAILABILITY_QUERY = gql`
    query VisitAvailability($input: ConfirmedAvailabilityInput!, $filter: AvailabilityFilter!) {
        confirmedAvailability(input: $input, filter: $filter) {
            ...FlexibleVisitAvailabilityFragment
        }
    }
    ${FLEXIBLE_VISIT_AVAILABILITY_FRAGMENT}
`;

export type FlexibleAvailabilityQueryType = {
    confirmedAvailability: FlexibleAvailabilityType[];
};

const SERVICE_BUNDLE_POSITION_FRAGMENT = gql`
    fragment ServiceBundlePositionFragment on ServiceBundlePosition {
        id
        startOffsetMinutes
        durationMinutes
        services {
            name
        }
    }
`;

type ServiceBundlePositionType = {
    id: string;
    startOffsetMinutes: number | null;
    durationMinutes: number;
    services: { name: string }[];
};

const GET_SERVICE_BUNDLE_QUERY = gql`
    query ServiceBundle($id: UUID!) {
        serviceBundle(id: $id) {
            id
            name
            description
            durationMinutes
            positions {
                ...ServiceBundlePositionFragment
            }
        }
    }
    ${SERVICE_BUNDLE_POSITION_FRAGMENT}
`;

type ServiceBundleQueryType = {
    serviceBundle: {
        id: string;
        name: string;
        description: string;
        durationMinutes: number;
        positions: ServiceBundlePositionType[];
    };
};

const SERVICE_BUNDLE_POSITION_ID_FRAGMENT = gql`
    fragment ServiceBundlePositionIdFragment on ServiceBundlePosition {
        id
    }
`;

type ServiceBundlePositionIdType = {
    id: string;
};

const GET_ELIGIBLE_CLINICIANS_QUERY = gql`
    query EligibleClinicians(
        $input: EligibilityForServiceBundleInput!
        $filters: EligibilityFilter
    ) {
        eligibilityForServiceBundleBeta(input: $input, filters: $filters) {
            byResource {
                eligibilityBoundResource {
                    ...ServiceBundlePositionIdFragment
                }
                clinicians {
                    ...ClinicianFragment
                }
            }
        }
    }
    ${CLINICIAN_FRAGMENT}
    ${SERVICE_BUNDLE_POSITION_ID_FRAGMENT}
`;

interface IEligibilityForServiceBundleInput {
    input: {
        organizationId: string;
        address: AddressInput;
        serviceAreaId: string;
        serviceBundleIds?: string[] | null;
        serviceIds?: string[] | null;
    };
    filters: Eligibility.Filters;
}

type EligibleCliniciansQueryType = {
    eligibilityForServiceBundleBeta: {
        byResource: {
            eligibilityBoundResource: ServiceBundlePositionIdType | null;
            clinicians: ClinicianFragmentType[];
        }[];
    };
};

const FlexibleSchedulingDialogContent = ({
    close,
    timezone,
}: {
    close: () => void;
    timezone: string;
}): ReturnComponent => {
    // Context
    const { patientId, demandOrgId, address, serviceAreaId, serviceBundleId, visitRequest } =
        useVisitBookingPageContext();

    const fallbackUuid = useFallbackToNewUuid(null);

    // UX state
    const showOnlyEligibleState = useInputState<boolean>(true);
    const showOnlyCareTeamState = useInputState<boolean>(false);

    // Input state
    const startTimeState = useInputState<Moment | null>(null, { required: true });

    // Queries
    const timeslotAlignmentMinutes = useTimeslotAlignmentMinutes();

    const { data: availabilityData } = useSuspenseQuery<FlexibleAvailabilityQueryType>(
        GET_FLEXIBLE_AVAILABILITY_QUERY,
        {
            variables: {
                input: {
                    // Who
                    patientId,
                    // Where
                    address: {
                        line1: address?.line1,
                        line2: address?.line2,
                        city: address?.city,
                        state: address?.state,
                        zipCode: address?.zipCode,
                    },
                    serviceAreaId,
                    // What
                    serviceBundleIds: serviceBundleId === '' ? null : [serviceBundleId],
                    appointments: visitRequest?.appointments.map((appointment) => ({
                        appointmentId: appointment.id,
                        serviceIds: appointment.services.map((service) => service.id),
                    })),
                },
                filter: {
                    // When
                    startDatetime: startTimeState.value
                        ?.tz(timezone)
                        .format('YYYY-MM-DDTHH:mm:ssZ'),
                },
            },
            skip: !startTimeState.value,
        },
    );
    const { data: eligibleCliniciansData } = useSuspenseQuery<
        EligibleCliniciansQueryType,
        IEligibilityForServiceBundleInput
    >(GET_ELIGIBLE_CLINICIANS_QUERY, {
        variables: {
            input: {
                organizationId: demandOrgId,
                // Where
                address: {
                    line1: address?.line1 ?? '',
                    line2: address?.line2 ?? '',
                    city: address?.city ?? '',
                    state: address?.state ?? '',
                    zipCode: address?.zipCode ?? '',
                },
                serviceAreaId,
                // What
                serviceBundleIds: serviceBundleId === '' ? null : [serviceBundleId],
                serviceIds:
                    visitRequest?.appointments
                        .map((appointment) => appointment.services)
                        .flatMap((services) => services.map((service) => service.id)) ?? null,
            },
            filters: {
                includeStrictlyEligibleOnly: false,
            },
        },
    });
    const { data: serviceBundleData } = useSuspenseQuery<ServiceBundleQueryType>(
        GET_SERVICE_BUNDLE_QUERY,
        serviceBundleId !== ''
            ? {
                  variables: {
                      id: serviceBundleId,
                  },
              }
            : skipToken,
    );
    const positions = serviceBundleData?.serviceBundle.positions ?? [
        {
            id: fallbackUuid,
            startOffsetMinutes: 0,
            durationMinutes: 0,
            services:
                visitRequest?.appointments.flatMap((appointment) =>
                    appointment.services.flatMap((service) => ({ name: service.name })),
                ) ?? [],
        },
    ];

    // Input state
    const assigneesState = usePotentialAssigneesStateHelper([]);
    const { checkValid, checkDifferent } = useJointInputState([startTimeState, assigneesState]);

    // Transformations
    const availableQhpsByPositionId = useMemo(() => {
        // TODO:(drew) refactor so that we don't need to use `QhpSimple`
        const cliniciansByAppointmentId: Record<string, QhpSimple[]> = {};
        availabilityData?.confirmedAvailability.forEach((avail) => {
            avail.potentialAppointmentAssignments.forEach((assignment) => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (!cliniciansByAppointmentId[assignment.clinicianAppointmentId]) {
                    cliniciansByAppointmentId[assignment.clinicianAppointmentId] = [];
                }
                if (assignment.clinician) {
                    cliniciansByAppointmentId[assignment.clinicianAppointmentId].push(
                        clinicianFragmentToQhpSimple(assignment.clinician),
                    );
                }
            });
        });
        return cliniciansByAppointmentId;
    }, [availabilityData]);

    const eligibleResourcesForPositions =
        eligibleCliniciansData.eligibilityForServiceBundleBeta.byResource.filter(
            (position) => position.eligibilityBoundResource?.id, // filter out null ids (which don't map to positions)
        );

    const eligibleCliniciansByPositionId = useMemo(() => {
        // TODO:(drew) refactor so that we don't need to use `QhpSimple`
        const eligibleCliniciansByPositionId: Record<string, QhpSimple[]> = {};
        eligibleResourcesForPositions.forEach((position) => {
            eligibleCliniciansByPositionId[position.eligibilityBoundResource?.id ?? fallbackUuid] =
                position.clinicians.map((clinician) => clinicianFragmentToQhpSimple(clinician));
        });
        return eligibleCliniciansByPositionId;
    }, [eligibleResourcesForPositions, fallbackUuid]);

    const assignableQhps = useMemo(
        () =>
            eligibleCliniciansData.eligibilityForServiceBundleBeta.byResource
                .flatMap((position) => position.clinicians)
                .map((clinician) => clinicianFragmentToQhpSimple(clinician)),
        [eligibleCliniciansData.eligibilityForServiceBundleBeta.byResource],
    );

    // Submitting
    const history = useHistory();
    const [createConfirmedVisit, { loading: confirmedLoading }] =
        useMutation<CreateConfirmedVisitMutationType>(CREATE_CONFIRMED_VISIT_MUTATION);
    const [createConfirmedVisitFromVisitRequest, { loading: confirmedFromVisitRequestLoading }] =
        useMutation<CreateConfirmedVisitFromVisitRequestMutationType>(
            CREATE_CONFIRMED_VISIT_FROM_VISIT_REQUEST_MUTATION,
        );

    const requestInFlight = confirmedLoading || confirmedFromVisitRequestLoading;
    const onSubmit = () => {
        if (!checkDifferent()) return;
        if (!checkValid()) return;
        if (requestInFlight) return;
        invariant(startTimeState.value !== null, 'Start time must be set');
        if (visitRequest) {
            void createConfirmedVisitFromVisitRequest({
                variables: {
                    input: {
                        visitRequestId: visitRequest.id,
                        // Who
                        patientId: patientId,
                        serviceAssignments: Object.entries(assigneesState.value).map(
                            ([_, qhp]) => ({
                                serviceIds: visitRequest.appointments
                                    .map((appointment) => appointment.services)
                                    .flatMap((services) => services.map((service) => service.id)),
                                clinicianId: qhp.uuid,
                            }),
                        ),
                        // What
                        serviceIds: visitRequest.appointments
                            .map((appointment) => appointment.services)
                            .flatMap((services) => services.map((service) => service.id)),
                        // Where
                        serviceAreaId: serviceAreaId,
                        address: {
                            line1: address?.line1,
                            line2: address?.line2,
                            city: address?.city,
                            state: address?.state,
                            zipCode: address?.zipCode,
                        },
                        // When
                        startDatetime: startTimeState.value
                            .tz(timezone)
                            .format('YYYY-MM-DDTHH:mm:ssZ'),
                    },
                },
                onCompleted: (data) => {
                    const visitId = data.createConfirmedVisitFromVisitRequest.id;
                    successToast('Visit created!');
                    history.push(`/visits/${visitId}`);
                },
            });
        } else {
            void createConfirmedVisit({
                variables: {
                    input: {
                        // Who
                        patientId: patientId,
                        serviceBundleAssignments: Object.entries(assigneesState.value).map(
                            ([positionId, qhp]) => ({
                                clinicianId: qhp.uuid,
                                serviceBundlePositionId: positionId,
                            }),
                        ),
                        // What
                        serviceBundleId: serviceBundleId === '' ? null : serviceBundleId,
                        // Where
                        serviceAreaId: serviceAreaId,
                        address: {
                            line1: address?.line1,
                            line2: address?.line2,
                            city: address?.city,
                            state: address?.state,
                            zipCode: address?.zipCode,
                        },
                        // When
                        startDatetime: startTimeState.value
                            .tz(timezone)
                            .format('YYYY-MM-DDTHH:mm:ssZ'),
                    },
                },
                onCompleted: (data) => {
                    successToast('Visit created!');
                    close();
                    const visitId = data.betaCreateConfirmedVisit.id;
                    history.push(`/visits/${visitId}`);
                },
            });
        }
    };

    return (
        <>
            <AxleDialogContent>
                <AxleCallout type={'warning'}>
                    Here you can select any clinician to perform a visit at any time, regardless of
                    their availability or eligibility. Availability and eligibility constraints are
                    not enforced here.
                </AxleCallout>
                <AxleMomentTimeField
                    label={`New Start Time (${getTimezoneAbbreviationFromIANA(timezone)})`}
                    inputState={startTimeState}
                    nullable={false}
                    timezone={timezone}
                    intervalMinutes={timeslotAlignmentMinutes}
                />
                <SmartPotentialVisitAssigneesField
                    label={'Assignees'}
                    assigneesState={assigneesState}
                    positions={positions.map((position) => ({
                        uuid: position.id,
                        services: position.services.map((service) => ({
                            name: service.name,
                        })),
                    }))}
                    availableQhpsByPositionId={availableQhpsByPositionId}
                    assignableQhps={assignableQhps}
                    eligibleQhpsByPositionId={eligibleCliniciansByPositionId}
                />
                {startTimeState.value !== null && (
                    <Box mt={4} mb={2}>
                        <AxleLabel label={'Clinician schedules on selected date'} />
                        <HorizontalStackInnerGrow spacing={1}>
                            <AxleCheckboxField
                                checkboxText={'Show only eligible clinicians'}
                                inputState={showOnlyEligibleState}
                            />
                            <AxleCheckboxField
                                checkboxText={'Show only care team clinicians'}
                                inputState={showOnlyCareTeamState}
                            />
                        </HorizontalStackInnerGrow>
                        <AxleSuspense skeletonHeight={SECTION_SKELETON_HEIGHT}>
                            <FlexibleSchedulingDialogsCalendarWrapper
                                patientId={patientId}
                                positions={positions.map((position) => ({
                                    uuid: position.id,
                                    startOffsetMinutes: position.startOffsetMinutes,
                                    durationMinutes: position.durationMinutes,
                                    services: position.services.map((service) => ({
                                        name: service.name,
                                    })),
                                }))}
                                assignableQhps={assignableQhps}
                                eligibleQhpsByPosition={eligibleCliniciansByPositionId}
                                newStartTime={startTimeState.value}
                                assignmentsByPosition={assigneesState.value}
                                showOnlyEligibleQhps={showOnlyEligibleState.value}
                                showOnlyCareTeamQhps={showOnlyCareTeamState.value}
                            />
                        </AxleSuspense>
                    </Box>
                )}
            </AxleDialogContent>
            <AxleDialogActions>
                <AxleDialogPrimaryButton
                    label={'Book'}
                    onClick={onSubmit}
                    enabled={
                        Object.keys(assigneesState.value).length > 0 &&
                        startTimeState.value !== null &&
                        !requestInFlight
                    }
                />
            </AxleDialogActions>
        </>
    );
};

export const FlexibleSchedulingDialog = ({
    isOpen,
    close,
    timezone,
}: {
    isOpen: boolean;
    close: () => void;
    timezone: string;
}): ReturnComponent => {
    return (
        <AxleDialog
            dataTestId='flexible-scheduling-dialog'
            isOpen={isOpen}
            close={close}
            title={'Flexible Scheduling'}
            width={'lg'}
        >
            <FlexibleSchedulingDialogContent close={close} timezone={timezone} />
        </AxleDialog>
    );
};
